import { i18n } from "@/services/locale/i18n";

import { IntakeConfirmationInterlude } from "../../intake-confirmation-interlude";
import { calculateBiddingModel } from "../../visitor-checks/calculateBiddingModel";
import { calculateLeadScore } from "../../visitor-checks/calculateLeadScore";
import { getVisitorStatusCode } from "../../visitor-checks/getVisitorStatusCode";
import { stepsNames } from "../config.const";
import type { SubPractices } from "../config.types";

import { hasCompletedIntakeQuestionnaire } from "./common-family";

export const familyElse: SubPractices = {
  firstScreen: "service-type",
  progressBarSections: [
    {
      name: "case-details",
      range: [20, 40],
    },
    {
      name: "preferences",
      range: [50, 70],
    },
    {
      name: "book-call",
      range: [80, 100],
    },
  ],
  screens: {
    "service-type": {
      next: async ({ answers }) => {
        const { serviceType } = answers;

        switch (serviceType?.toLowerCase()) {
          case "none of the above":
            return "zip-code";

          case "child protective services involvement":
            return "child-protective-services";

          default:
            return "out-of-scope";
        }
      },
      progressBarSection: "case-details",
    },
    "zip-code": {
      next: async ({ answers, meta }) => {
        const { state } = answers;
        const { skip } = meta;

        if (skip) {
          return "state-selection";
        }

        if (state === "unsupported") {
          return "unsupported-county";
        }

        return "location-confirmation";
      },
      progressBarSection: "preferences",
    },
    "state-selection": {
      next: async ({ answers }) => {
        const { state } = answers;

        if (state === "unsupported") {
          return "unsupported";
        }

        return "location-confirmation";
      },
      progressBarSection: "preferences",
    },
    unsupported: {
      next: async () => "unsupported-confirmed",
      sanitize: true,
    },
    "unsupported-confirmed": {
      next: async () => "unsupported-confirmed",
    },
    "location-confirmation": {
      next: async () => "case-urgency",
      progressBarSection: "preferences",
    },
    "main-goal": {
      next: async () => "case-urgency",
      progressBarSection: "preferences",
    },
    "case-urgency": {
      next: async () => "more-info",
      progressBarSection: "preferences",
    },
    "more-info": {
      next: async ({ answers }) => {
        calculateLeadScore({
          answers,
          step: stepsNames["more-info"],
        });
        await calculateBiddingModel({
          answers,
          step: stepsNames["more-info"],
        });

        return "create-meeting";
      },
      progressBarSection: "preferences",
    },
    "create-meeting": {
      next: async ({ answers }) =>
        answers?.eventStartTime === "call_me_now"
          ? "contact-info-call-now"
          : "contact-info",
      progressBarSection: "book-call",
    },
    "contact-info": {
      next: async ({ answers }) => {
        await calculateBiddingModel({
          answers,
          step: "contact-info",
        });

        return "intake-confirmation";
      },
      sanitize: true,
      isAuth: true,
      renderInterlude: () => (
        <IntakeConfirmationInterlude
          heading={i18n.t("FUNNEL_INTERLUDE_CONFIRMING_YOUR_CALL")}
        />
      ),
      progressBarSection: "book-call",
    },

    "contact-info-call-now": {
      next: async ({ answers }) => {
        await calculateBiddingModel({
          answers,
          step: "contact-info-call-now",
        });
        return "intake-confirmation-call-now";
      },
      sanitize: true,
      isAuth: true,
      renderInterlude: () => (
        <IntakeConfirmationInterlude
          heading={i18n.t("FUNNEL_INTERLUDE_CONFIRMING_YOUR_CALL")}
        />
      ),
    },

    "intake-confirmation": {
      next: async () => undefined,
    },

    "intake-confirmation-call-now": {
      next: async () => undefined,
    },
    "attorney-qualities": {
      next: async ({ answers }) => {
        if (answers?.leadSource === "legal-plan") {
          return "opposing-party";
        }
      },
    },
    "child-protective-services": {
      next: async ({ answers }) => {
        const { childProtectiveServices } = answers;
        return childProtectiveServices?.toLowerCase() === "yes"
          ? "out-of-scope"
          : "zip-code";
      },
      progressBarSection: "case-details",
    },
    "opposing-party": {
      next: async ({ answers }) => {
        if (
          answers?.leadSource === "legal-plan" ||
          hasCompletedIntakeQuestionnaire()
        ) {
          const statusCode = await getVisitorStatusCode({
            answers,
            currentStep: stepsNames["opposing-party"],
          });

          if (statusCode) {
            answers.visitorStatusCode = statusCode;
            if (statusCode !== "ELG1" && statusCode !== "ERROR") {
              return "out-of-scope";
            }
          }
          return "book-lss";
        }
      },
      isUpdate: true,
    },
    "book-lss": {
      next: async () => {
        /*
        This step is used for:
         - Legal plan leads
         - Pre-clients with completed intake questionnaires 
      */
        return "lss-checkout";
      },
    },
    "lss-checkout": {
      next: async ({ meta }) => {
        const { checkoutStatus } = meta;
        return checkoutStatus;
      },
      isUpdate: true,
    },
    "slot-is-taken": {
      next: async () => "book-lss",
    },
    "payment-declined": {
      next: async () => "lss-checkout",
    },
    "lss-confirmation": {
      next: async () => undefined,
    },
    "out-of-scope": {
      next: async () => undefined,
    },
  },
};
